import { Fragment, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatDistributionCenterName } from '@/utils/formatters'
import {
  formatShortDate,
  STAGE_GROUPS,
  STATUS_CELL_CLASSES,
  type EventProductFlowHierarchyNode,
  type MilestoneColumnDef,
  type MilestoneValue,
} from './eventProductFlowMockData'

interface Props {
  groups: EventProductFlowHierarchyNode[]
}

const LABEL_COL_WIDTH = 260
const MILESTONE_COL_WIDTH = 130

function MilestoneCell({ value }: { value: MilestoneValue }) {
  return (
    <td
      style={{ width: MILESTONE_COL_WIDTH, minWidth: MILESTONE_COL_WIDTH }}
      className={cn(
        'border-r border-b border-border px-2 py-1.5 align-top text-[11px] leading-tight',
        STATUS_CELL_CLASSES[value.status]
      )}
    >
      <div>Expected: {formatShortDate(value.expectedDate)}</div>
      <div>Projected: {formatShortDate(value.projectedDate)}</div>
      <div>Complete: {value.completePct}%</div>
    </td>
  )
}

function LabelCell({
  children,
  bgClass,
  bold,
  onClick,
}: {
  children: React.ReactNode
  bgClass: string
  bold?: boolean
  onClick?: () => void
}) {
  return (
    <td
      onClick={onClick}
      style={{
        width: LABEL_COL_WIDTH,
        minWidth: LABEL_COL_WIDTH,
        left: 0,
      }}
      className={cn(
        'sticky z-10 border-r border-b border-border px-3 py-2 text-xs',
        bgClass,
        bold && 'font-semibold',
        onClick && 'cursor-pointer hover:brightness-95'
      )}
    >
      {children}
    </td>
  )
}

export function EventProductFlowTable({ groups }: Props) {
  const [expandedNodeKeys, setExpandedNodeKeys] = useState<Set<string>>(
    new Set()
  )

  const toggle = (key: string) => {
    setExpandedNodeKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Only render groups (and their columns) that are actually present.
  const columnKeySet = new Set(
    groups[0] ? Object.keys(groups[0].aggregate) : []
  )
  const activeStageGroups = STAGE_GROUPS.map(g => ({
    ...g,
    columns: g.columns.filter(c => columnKeySet.has(c.key)),
  })).filter(g => g.columns.length > 0)

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
      <div className="max-h-[640px] overflow-auto">
        <table className="border-separate text-xs" style={{ borderSpacing: 0 }}>
          <thead>
            <tr>
              <th
                rowSpan={2}
                style={{
                  width: LABEL_COL_WIDTH,
                  minWidth: LABEL_COL_WIDTH,
                  left: 0,
                }}
                className="sticky top-0 z-30 border-r border-b border-border bg-white px-3 py-2 text-left text-xs font-semibold text-foreground"
              >
                Season / Event / POG Group / POG ID / DC / PO / SKU
              </th>
              {activeStageGroups.map(group => (
                <th
                  key={group.key}
                  colSpan={group.columns.length}
                  className="sticky top-0 z-20 border-r border-b border-border bg-slate-100 px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-foreground"
                >
                  {group.label}
                </th>
              ))}
            </tr>
            <tr>
              {activeStageGroups.flatMap(group =>
                group.columns.map(col => (
                  <th
                    key={col.key}
                    style={{
                      width: MILESTONE_COL_WIDTH,
                      minWidth: MILESTONE_COL_WIDTH,
                    }}
                    className="sticky top-9 z-20 border-r border-b border-border bg-slate-50 px-2 py-2 text-left text-[11px] font-semibold text-foreground"
                  >
                    {col.label}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            <HierarchyRows
              nodes={groups}
              columns={activeStageGroups.flatMap(g => g.columns)}
              expandedNodeKeys={expandedNodeKeys}
              onToggle={toggle}
            />
          </tbody>
        </table>
      </div>
    </div>
  )
}

function HierarchyRows({
  nodes,
  columns,
  expandedNodeKeys,
  onToggle,
}: {
  nodes: EventProductFlowHierarchyNode[]
  columns: MilestoneColumnDef[]
  expandedNodeKeys: Set<string>
  onToggle: (key: string) => void
}) {
  return (
    <>
      {nodes.map(node => {
        const isExpanded = expandedNodeKeys.has(node.key)
        const canExpand = node.children.length > 0
        const isSeason = node.level === 'season'
        const isSku = node.level === 'sku'
        const bgClass = isSeason ? 'bg-slate-50' : 'bg-white'

        return (
          <Fragment key={node.key}>
            <tr
              className={cn(
                bgClass,
                canExpand ? 'hover:brightness-95' : 'hover:bg-muted/20'
              )}
            >
              <LabelCell
                bgClass={bgClass}
                bold={!isSku}
                onClick={canExpand ? () => onToggle(node.key) : undefined}
              >
                <span
                  className={cn(
                    'inline-flex items-center gap-1',
                    isSku && 'text-muted-foreground'
                  )}
                  style={{ marginLeft: node.depth * 16 }}
                >
                  {canExpand ? (
                    isExpanded ? (
                      <ChevronDown className="h-3 w-3 shrink-0" />
                    ) : (
                      <ChevronRight className="h-3 w-3 shrink-0" />
                    )
                  ) : (
                    <span className="h-3 w-3 shrink-0" aria-hidden="true" />
                  )}
                  {node.level === 'dc'
                    ? formatDistributionCenterName(node.label)
                    : node.label}
                </span>
              </LabelCell>
              {columns.map(col => (
                <MilestoneCell key={col.key} value={node.aggregate[col.key]} />
              ))}
            </tr>
            {isExpanded && canExpand ? (
              <HierarchyRows
                nodes={node.children}
                columns={columns}
                expandedNodeKeys={expandedNodeKeys}
                onToggle={onToggle}
              />
            ) : null}
          </Fragment>
        )
      })}
    </>
  )
}
